import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authLoginApi } from '@store/rtk/auth';
import { TableData } from '@uikit/Tables/types';
import { selectItemType } from '@src/types';
import { formatUTCToLocal, removeCounty } from '@src/utils';
import { auditLogsApi } from '@store/rtk';
import { sortPermissionRoleTypes } from '@utils/helpers';
import { formatFieldTitle, getPermissionLabels } from '@utils/inputTitles';
import {
  BuildingBoldIcon,
  CheckCircleOutlineIcon,
  CircleCloseIcon,
  UserIcon,
} from '@src/svg';
import { sessionSetter } from '@src/utils/storage';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';
import { STORAGE_VERSION_KEY, UserType } from '@page/ConfigWrapper';
import { CONTRACTOR_USER, MOBILE_USER, WEB_USER } from '@routes/helpers';

export type usersType = {
  active: boolean;
  email: string;
  firstName: string;
  id: 5;
  jobTitle: string;
  lastName: string;
  phone: string;
  roles: selectItemType | null;
  sms: boolean;
  type: selectItemType | null;
  status: selectItemType | null;
  relation: selectItemType | null;
  image: string;
  isLocked: boolean;
  roleType?: string;
  permission?: string;
};

export interface Ability {
  id: number;
  name: string;
}

export interface Permission {
  id: number;
  typeId: number;
  name: string;
  module: string;
  lite: boolean;
  abilities: Ability[];
}

export interface Option {
  id: number;
  value: string;
  label: string;
}

export interface User {
  id: number;
  lastName: string;
  firstName: string;
  email: string;
  jobTitle: string;
  phone: string;
  active: boolean;
  sms: boolean;
  status: string;
  associated: number;
  image: string;
  type: Option;
  permissions: Permission[];
  roles: Option;
  password: string | null;
  ssoProviderType?: string[];
}

type state = {
  userInfo: User | null;
  tempToken: string | null;
  message: null | string;
  newPermissions: any;
  newPermissionsPath: string | null;
  permissionsTableData: TableData;
  profilePermissionsTableData: TableData;
  userViewsData: TableData;
  userLoginsData: TableData;
  userActivityData: TableData;
  userAuditData: TableData;
  activeUrl: null | string;
  userDetailsData: any;
  isOpenAccountSettings: boolean;
  isNotificationRead: boolean;
  filterTable: any;
  usersTableData: TableData;
  usersRolesTableData: TableData;
  usersRolesPageSize: number;
  isContractorVersion: boolean;
  userType:
    | typeof WEB_USER
    | typeof CONTRACTOR_USER
    | typeof MOBILE_USER
    | 'payerPortal'
    | 'memberPortal';
};

const rawUserInfo = sessionSetter.getItem('userInfo');

const {
  permissionsTableColumns,
  userViewsColumns,
  userLoginsColumns,
  userActivityColumns,
  userAuditColumns,
  usersTableColumns,
  usersTableFiltrationColumns,
  usersRolesTableColumns,
} = TABLE_COLUMNS;

const defaultState: state = {
  userInfo:
    rawUserInfo && typeof rawUserInfo === 'string'
      ? JSON.parse(rawUserInfo)
      : (rawUserInfo ?? null),
  tempToken: null,
  message: null,
  newPermissions: null,
  newPermissionsPath: null,
  isContractorVersion: false,
  usersRolesPageSize: 10,
  permissionsTableData: {
    columns: permissionsTableColumns,
    rows: [],
    // size: 10,
  },
  profilePermissionsTableData: {
    columns: permissionsTableColumns,
    rows: [],
    size: 10,
  },
  userViewsData: {
    columns: userViewsColumns,
    rows: [],
    size: 5,
  },
  userLoginsData: {
    columns: userLoginsColumns,
    rows: [],
    size: 5,
  },
  userActivityData: {
    columns: userActivityColumns,
    rows: [],
    size: 5,
  },
  userAuditData: {
    columns: userAuditColumns,
    rows: [],
    size: 5,
  },
  activeUrl: null,
  userDetailsData: null,
  isOpenAccountSettings: false,
  isNotificationRead: false,
  filterTable: {},
  usersTableData: {
    columns: usersTableColumns,
    columnsForFilter: usersTableFiltrationColumns,
    rows: [],
    size: 5,
  },
  usersRolesTableData: {
    columns: usersRolesTableColumns,
    rows: [],
    size: 5,
  },
  userType: (localStorage.getItem(STORAGE_VERSION_KEY) as UserType) ?? WEB_USER,
};

const mapPermissionToRow = (el) => {
  const abilities = el.abilities ?? [];
  const findAbility = (field: string) => {
    return abilities.find(
      (elem) => (typeof elem === 'string' ? elem : elem.name) === field
    ) ? (
      <CheckCircleOutlineIcon width={16} height={16} />
    ) : null;
    //TODO its ? icon for change permissions in front

    //   (
    //   <HelpIcon width={20} height={20} color="red" />
    // );
  };
  const { module, tab } = getPermissionLabels(el);

  return {
    module,
    tab,
    read: findAbility('Read'),
    // only the Contractor Portal table has a Special column
    special: findAbility('Document'),
    // TODO check this in the future and delete
    // special:
    //   findAbility('Document') ||
    //   findAbility('Audit') ||
    //   findAbility('Create') ? (
    //     <CheckCircleOutlineIcon />
    //   ) : null,

    edit: findAbility('Edit'),
    noAccess: abilities.length ? null : (
      <CircleCloseIcon width={16} height={16} />
    ),
  };
};

export const usersSlice = createSlice({
  name: 'users',
  initialState: defaultState,
  reducers: {
    resetPermissionsTableFilter: (state) => {
      state.permissionsTableData.filter = {};
    },
    setUserInfo: (state, action) => {
      const isSameUserInfo =
        Boolean(state.userInfo) &&
        Boolean(action.payload) &&
        JSON.stringify(state.userInfo) === JSON.stringify(action.payload);

      if (!isSameUserInfo) {
        state.userInfo = action.payload;
      }

      if (!action.payload?.associated) {
        state.isContractorVersion = false;
      }
    },

    setNewPermissions: (
      state,
      action: PayloadAction<{ permissions: any; path: string }>
    ) => {
      state.newPermissions = action.payload.permissions;
      state.newPermissionsPath = action.payload.path;
    },
    setUserType: (state, action: PayloadAction<any>) => {
      state.userType = action.payload;
    },
    setActiveURL: (state, action: PayloadAction<string | null>) => {
      state.activeUrl = action.payload;
    },
    setIsContractorVersion: (state, action: PayloadAction<boolean>) => {
      state.isContractorVersion = action.payload;
    },
    setIsOpenAccountSettings: (state, action: PayloadAction<boolean>) => {
      state.isOpenAccountSettings = action.payload;
    },
    setIsNotificationRead: (state, action: PayloadAction<boolean>) => {
      state.isNotificationRead = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        authLoginApi.endpoints.getContractorVersion.matchFulfilled,
        (state, { payload }) => {
          state.isContractorVersion = payload;
        }
      )
      .addMatcher(
        authLoginApi.endpoints.getUsers.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.usersTableData = {
              ...state.usersTableData,
              ...payload,
              rows: [
                ...payload?.content?.map((el) => ({
                  ...el,
                  nameForTable: {
                    image: el.image,
                    firstName: el.firstName,
                    lastName: el.lastName,
                    permission: el.permission,
                    status: el.status,
                  },
                  // TODO this should be come from backend!!!
                  // typeForTable: el.type?.value,
                  typeForTable: el?.roleType,
                  active: el.active,
                  isLocked: el.isLocked,
                })),
              ],

              requestTablePageChange: authLoginApi.endpoints.getUsers,
              requestStatusChange: authLoginApi.endpoints.createOrUpdateUser,
            };
          }
        }
      )
      .addMatcher(
        authLoginApi.endpoints.getUserPermissionsTypeSelectTable.matchFulfilled,
        (state, { payload }) => {
          if (payload && Array.isArray(payload?.content)) {
            const total = payload.totalElements || 0;
            const sortedRoles = sortPermissionRoleTypes(payload.content);

            const pageSize =
              payload.size && payload.size <= total
                ? payload.size
                : Math.min(total, 10);

            state.usersRolesTableData = {
              ...state.usersRolesTableData,
              rows: sortedRoles.map((el) => ({
                ...el,
                nameForTable: {
                  icon:
                    el.label === 'Payer' ? <BuildingBoldIcon /> : <UserIcon />,
                  firstName: el.value,
                },
                // typeForTable: capitalizeWords(
                //   formatFieldTitle(el?.label.toLowerCase())
                // ),
                typeForTable: el?.label,
              })),
              ...payload,
              size: pageSize,
              requestTablePageChange:
                authLoginApi.endpoints.getUserPermissionsTypeSelectTable,
            };
          }
        }
      )
      .addMatcher(
        authLoginApi.endpoints.getPermissionsById.matchFulfilled,
        (state, { payload }: any) => {
          if (payload && payload.content) {
            state.usersRolesPageSize = payload.size;
            state.permissionsTableData = {
              ...state.permissionsTableData,
              rows: payload.content.map(mapPermissionToRow),

              ...payload,
              requestTablePageChange: authLoginApi.endpoints.getPermissionsById,
            };
          }
        }
      )
      .addMatcher(
        authLoginApi.endpoints.getProfilePermissions.matchFulfilled,
        (state, { payload }: any) => {
          state.profilePermissionsTableData = {
            ...state.profilePermissionsTableData,
            rows: payload.content.map(mapPermissionToRow),
            ...payload,
            requestTablePageChange:
              authLoginApi.endpoints.getProfilePermissions,
          };
        }
      )
      .addMatcher(
        auditLogsApi.endpoints.getAuditView.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.userViewsData = {
              ...state.userViewsData,
              rows: [
                ...payload.content.map((el) => ({
                  ...el,
                  viewName: formatFieldTitle(el.viewName ? el.viewName : ''),
                  resource: el.resource,
                  timestamp: formatUTCToLocal(el.timestamp),
                })),
              ],
              ...payload,
              requestTablePageChange: auditLogsApi.endpoints.getAuditView,
            };
          }
        }
      )
      .addMatcher(
        auditLogsApi.endpoints.getUserLogins.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.userLoginsData = {
              ...state.userLoginsData,
              rows: [
                ...payload.content.map((el) => ({
                  ...el,
                  location: [el.city, el.country]
                    .filter(
                      (value) =>
                        typeof value === 'string' && value.trim().length > 0
                    )
                    .map(removeCounty)
                    .join(', '),
                  timestamp: formatUTCToLocal(el.timestamp),
                })),
              ],
              ...payload,
              requestTablePageChange: auditLogsApi.endpoints.getUserLogins,
            };
          }
        }
      )
      .addMatcher(
        auditLogsApi.endpoints.getAdminUserActivity.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.userActivityData = {
              ...state.userActivityData,
              rows: payload.content.map((el) => {
                const cleaned = {} as Record<string, any>;

                Object.entries(el).forEach(([key, value]) => {
                  if (
                    value !== null &&
                    value !== undefined &&
                    value !== 'null'
                  ) {
                    cleaned[key] = value;
                  }
                });

                if (cleaned.resource)
                  cleaned.resource = formatFieldTitle(cleaned.resource);
                if (cleaned.field)
                  cleaned.field = formatFieldTitle(cleaned.field);
                if (cleaned.actionTime)
                  cleaned.actionTime = formatUTCToLocal(cleaned.actionTime);

                return cleaned;
              }),
              ...payload,
              requestTablePageChange:
                auditLogsApi.endpoints.getAdminUserActivity,
            };
          }
        }
      )
      .addMatcher(
        auditLogsApi.endpoints.getAdminUserLogs.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.userAuditData = {
              ...state.userAuditData,
              rows: [
                ...payload.content.map((el) => ({
                  ...el,
                  field: formatFieldTitle(el.field ?? ''),
                  actionTime: formatUTCToLocal(el.actionTime),
                })),
              ],
              ...payload,
              requestTablePageChange: auditLogsApi.endpoints.getAdminUserLogs,
            };
          }
        }
      )

      .addMatcher(
        authLoginApi.endpoints.getUserById.matchFulfilled,
        (state, { payload }) => {
          state.userDetailsData = payload;
        }
      );
  },
});

export const {
  resetPermissionsTableFilter,
  setUserInfo,
  setNewPermissions,
  setActiveURL,
  setIsContractorVersion,
  setIsOpenAccountSettings,
  setIsNotificationRead,
  setUserType,
} = usersSlice.actions;
export const selectUsers = (state) => state.users;
export const usersReducer = usersSlice.reducer;
